export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'fair' | 'good' | 'strong';
}

export interface PasswordCriteria {
  minLength: boolean;
  hasLowerCase: boolean;
  hasUpperCase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

/**
 * Password policy validation utility
 * Requirements:
 * - At least 8 characters long
 * - Contains at least one lowercase letter (a-z)
 * - Contains at least one uppercase letter (A-Z)
 * - Contains at least one number (0-9)
 * - Contains at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)
 */
export const validatePassword = (password: string): PasswordValidationResult => {
  const errors: string[] = [];
  
  // Check minimum length (8 characters)
  const hasMinLength = password.length >= 8;
  if (!hasMinLength) {
    errors.push('Password must be at least 8 characters long');
  }
  
  // Check for lowercase letter
  const hasLowerCase = /[a-z]/.test(password);
  if (!hasLowerCase) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  // Check for uppercase letter
  const hasUpperCase = /[A-Z]/.test(password);
  if (!hasUpperCase) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  // Check for number
  const hasNumber = /\d/.test(password);
  if (!hasNumber) {
    errors.push('Password must contain at least one number');
  }
  
  // Check for special character
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password);
  if (!hasSpecialChar) {
    errors.push('Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)');
  }
  
  const isValid = hasMinLength && hasLowerCase && hasUpperCase && hasNumber && hasSpecialChar;
  
  // Calculate password strength
  let strength: 'weak' | 'fair' | 'good' | 'strong' = 'weak';
  const criteriaMetCount = [hasMinLength, hasLowerCase, hasUpperCase, hasNumber, hasSpecialChar].filter(Boolean).length;
  
  if (criteriaMetCount === 5 && password.length >= 12) {
    strength = 'strong';
  } else if (criteriaMetCount === 5) {
    strength = 'good';
  } else if (criteriaMetCount >= 3) {
    strength = 'fair';
  }
  
  return {
    isValid,
    errors,
    strength
  };
};

/**
 * Get individual criteria status for password policy checklist
 */
export const getPasswordCriteria = (password: string): PasswordCriteria => {
  return {
    minLength: password.length >= 8,
    hasLowerCase: /[a-z]/.test(password),
    hasUpperCase: /[A-Z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)
  };
};

/**
 * Get password strength color for UI styling (text)
 */
export const getStrengthColor = (strength: 'weak' | 'fair' | 'good' | 'strong'): string => {
  switch (strength) {
    case 'weak':
      return 'text-text-error-primary';
    case 'fair':
      return 'text-text-warning-primary';
    case 'good':
      return 'text-text-brand-primary';
    case 'strong':
      return 'text-text-success-primary';
    default:
      return 'text-text-tertiary';
  }
};

/**
 * Get password strength background color for progress bars
 */
export const getStrengthBgColor = (strength: 'weak' | 'fair' | 'good' | 'strong'): string => {
  switch (strength) {
    case 'weak':
      return 'bg-fg-error-secondary';
    case 'fair':
      return 'bg-fg-warning-secondary';
    case 'good':
      return 'bg-fg-success-secondary';
    case 'strong':
      return 'bg-fg-success-secondary';
    default:
      return 'bg-bg-tertiary';
  }
};
